import { inject, injectable } from 'inversify'
import type { InternalTrackingPayload, TrackableConfig, TrackingEvent, Trackable } from '@/services/tracking/types'
import TmLogicError from '@/core/error/tmLogicError'
import { SERVICE_TYPES } from '@/core/container/types'
// TODO: Fix ignored tp/forbid-import-composable-to-service
// eslint-disable-next-line tp/using-vue-in-services-restriction,tp/forbid-import-composable-to-service
import type { Plugin } from '@/composition/vue/compositionApi'
import { isProd, isTest } from '@/utils/system'
import ConfigReader from '@/core/configReader'
import type { HttpService } from '@/services/transport/httpService'
import type { AbstractEndpointsInterface } from '@/services/endpointsService'
import type WindowService from '@/services/browser/windowService'
import type { MicrosoftUetService } from '@/services/tracking/microsoftUetService'
import type CookieConsentService from '@/services/browser/cookieConsentService'

@injectable()
export default class TrackingService {
  private trackers: Trackable[] = []

  private isLoaded = false

  constructor(
    @inject(SERVICE_TYPES.Api) protected readonly httpService: HttpService,
    @inject(SERVICE_TYPES.EndpointsService) protected readonly endpointsService: AbstractEndpointsInterface,
    @inject(SERVICE_TYPES.WindowService) protected readonly windowService: WindowService,
    @inject(SERVICE_TYPES.ZendeskService) protected readonly zendeskService: Trackable,
    @inject(SERVICE_TYPES.ShareASaleService) protected readonly shareASaleService: Trackable,
    @inject(SERVICE_TYPES.FirstPromoterService) protected readonly firstPromoterService: Trackable,
    @inject(SERVICE_TYPES.PostHogService) protected readonly postHogService: Trackable,
    @inject(SERVICE_TYPES.GoogleAnalyticsService) protected readonly googleAnalyticsService: Trackable,
    @inject(SERVICE_TYPES.MicrosoftUetService)
    protected readonly microsoftUetService: MicrosoftUetService,
    @inject(SERVICE_TYPES.GartnerConversionService) protected readonly gartnerConversionService: Trackable,
    @inject(SERVICE_TYPES.MetaPixelTrackingService) protected readonly metaPixelTrackingService: Trackable,
    @inject(SERVICE_TYPES.OpenAiPixelService) protected readonly openAiPixelService: Trackable,
    @inject(SERVICE_TYPES.TikTokPixelService) protected readonly tikTokPixelService: Trackable,
    @inject(SERVICE_TYPES.CookieConsentService)
    protected readonly cookieConsentService: CookieConsentService,
  ) {}

  public registerApp(app: { use: (plugin: Plugin, config: TrackableConfig) => void }): void {
    this.initialize()
    this.windowService.self().onload = () => {
      if (!this.isLoaded) {
        this.isLoaded = true

        const trackersToInit = this.getTrackers().filter((t) => {
          // Microsoft Ads tracker should be initialized only when marketing consent is given
          return t !== this.microsoftUetService || this.cookieConsentService.hasMarketingConsent()
        })

        trackersToInit.forEach((tracker: Trackable) => {
          app.use(tracker.getPlugin(), tracker.getConfig())
        })
      }
    }
  }

  public initialize(): void {
    this.addTracker(this.zendeskService)
      .addTracker(this.shareASaleService)
      .addTracker(this.firstPromoterService)
      .addTracker(this.postHogService)
      .addTracker(this.googleAnalyticsService)
      .addTracker(this.microsoftUetService)
      .addTracker(this.gartnerConversionService)
      .addTracker(this.metaPixelTrackingService)
      .addTracker(this.openAiPixelService)
      .addTracker(this.tikTokPixelService)
  }

  public addTracker(tracker: Trackable): TrackingService {
    const config = ConfigReader.config()
    if (!this.isSupportedEnvironment() && !config.tracking.forceTracking) {
      return this
    }
    if (this.getTrackers().includes(tracker)) {
      throw new TmLogicError('Already existed tracker cannot be added')
    }

    this.getTrackers().push(tracker)
    return this
  }

  public getTrackers(): Trackable[] {
    return this.trackers
  }

  /**
   * Do not use it as asynchronous function await track(), it will block code execution for tracking server responses time
   */
  public track(event: TrackingEvent): void {
    const internalTrackingPath = this.endpointsService.getPath('auditLogs')
    let trackingTraits: Record<string, any> = {}
    if (event.properties) {
      trackingTraits = event.properties
    }
    trackingTraits.category = event.category

    try {
      this.httpService.post(internalTrackingPath, {
        eventName: event.name,
        properties: trackingTraits,
      } as InternalTrackingPayload)
    } catch {
      // do nothing if tracking failed for some reason
    }
  }

  public cleanUp() {
    this.getTrackers().forEach((tracker) => {
      tracker.cleanUp()
    })
  }

  private isSupportedEnvironment(): boolean {
    return isTest || isProd
  }
}
